import React, { useCallback } from 'react';
import { InputNumber, Select, Radio, Divider } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  useAreas,
  useMeasurementUnits,
} from '../../hooks/useSprayCalculatorData';
import { APPLICATION_TYPES } from '../../constants';
import { HtmlContent } from '@client/utils/html_content';

/**
 * Step 2 - Area Component
 * Form for total area, area unit, application type, and band configuration
 *
 * @param {Object} props
 * @param {Object} props.selections - Current selections from useSprayCalculator
 * @param {Function} props.setTotalArea - Setter for total area value
 * @param {Function} props.setAreaUnitId - Setter for area unit (accepts unitId, unitName)
 * @param {Function} props.setApplicationType - Setter for application type
 * @param {Function} props.setBandPercentage - Setter for band percentage
 * @param {Function} props.setRowSpacing - Setter for row spacing value
 * @param {Function} props.setRowSpacingUnitId - Setter for row spacing unit (accepts unitId, unitName)
 * @param {Function} props.setBandWidth - Setter for band width value
 * @param {Function} props.setBandWidthUnitId - Setter for band width unit (accepts unitId, unitName)
 * @param {Object} props.enrichedData - Enriched data with unit labels
 */
const Step2Area = React.memo(
  ({
    selections,
    setTotalArea,
    setAreaUnitId,
    setApplicationType,
    setBandPercentage,
    setRowSpacing,
    setRowSpacingUnitId,
    setBandWidth,
    setBandWidthUnitId,
    enrichedData,
  }) => {
    const { t } = useTranslation('sprayCalculator');
    const { data: areas = [], isLoading: isLoadingAreas } = useAreas();
    const {
      data: measurementUnits = [],
      isLoading: isLoadingMeasurementUnits,
    } = useMeasurementUnits();

    const handleTotalAreaChange = useCallback(
      (value) => {
        setTotalArea(value);
      },
      [setTotalArea]
    );

    const handleAreaUnitChange = useCallback(
      (unitId, option) => {
        setAreaUnitId(unitId, option?.rawLabel || null);
      },
      [setAreaUnitId]
    );

    const handleApplicationTypeChange = useCallback(
      (e) => {
        setApplicationType(e.target.value);
      },
      [setApplicationType]
    );

    const handleBandPercentageChange = useCallback(
      (value) => {
        setBandPercentage(value);
      },
      [setBandPercentage]
    );

    const handleRowSpacingChange = useCallback(
      (value) => {
        setRowSpacing(value);
      },
      [setRowSpacing]
    );

    const handleRowSpacingUnitChange = useCallback(
      (unitId, option) => {
        setRowSpacingUnitId(unitId, option?.rawLabel || null);
      },
      [setRowSpacingUnitId]
    );

    const handleBandWidthChange = useCallback(
      (value) => {
        setBandWidth(value);
      },
      [setBandWidth]
    );

    const handleBandWidthUnitChange = useCallback(
      (unitId, option) => {
        setBandWidthUnitId(unitId, option?.rawLabel || null);
      },
      [setBandWidthUnitId]
    );

    const areaUnitOptions = areas.map((item) => ({
      value: item.id,
      label: <HtmlContent html={item.name} />,
      rawLabel: item.name,
    }));

    const measurementUnitOptions = measurementUnits.map((unit) => ({
      value: unit.id,
      label: <HtmlContent html={unit.name} />,
      rawLabel: unit.name,
    }));

    const isBandApplication =
      selections.applicationType === APPLICATION_TYPES.BAND;

    return (
      <div className="flex flex-col gap-4 sm:gap-6 w-full">
        <h3 className="text-xl sm:text-2xl lg:text-[30px] font-medium text-center normal-case">
          {t('area_title')}
        </h3>
        <p className="text-xs sm:text-sm text-[#000]">
          {t('area_description')}
        </p>

        {/* Container 1: Total area + unit (two-column like Step 1) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 w-full p-3 sm:p-4 lg:p-6 rounded-2xl bg-[#FAFAFA]">
          <div className="w-full flex flex-col gap-1.5">
            <label className="text-sm font-medium text-[#141414]">
              {t('area_total_label')}
            </label>
            <InputNumber
              value={
                selections.totalArea ? parseFloat(selections.totalArea) : null
              }
              onChange={handleTotalAreaChange}
              placeholder={t('area_total_placeholder')}
              min={0}
              className="w-full"
              size="large"
            />
          </div>

          <div className="w-full flex flex-col gap-1.5">
            <label className="text-sm font-medium text-[#141414]">
              {t('area_unit_label')}
            </label>
            <Select
              showSearch
              filterOption={false}
              value={selections.areaUnitId || undefined}
              onChange={handleAreaUnitChange}
              placeholder={t('area_unit_placeholder')}
              options={areaUnitOptions}
              loading={isLoadingAreas}
              className="w-full"
              size="large"
            />
          </div>
        </div>

        {/* Container 2: Application type */}
        <div className="w-full p-3 sm:p-4 lg:p-6 rounded-2xl bg-[#FAFAFA]">
          <div className="w-full flex flex-col gap-1.5">
            <label className="text-sm font-medium text-[#141414]">
              {t('area_application_type_label')}
            </label>
            <Radio.Group
              value={selections.applicationType || undefined}
              onChange={handleApplicationTypeChange}
              className="flex gap-4"
            >
              <Radio value={APPLICATION_TYPES.FULL_SURFACE}>
                {t('area_full_surface')}
              </Radio>
              <Radio value={APPLICATION_TYPES.BAND}>{t('area_band')}</Radio>
            </Radio.Group>
          </div>
        </div>

        {/* Band configuration (conditional) */}
        {isBandApplication && (
          <div className="w-full flex flex-col gap-4 p-3 sm:p-4 lg:p-6 rounded-2xl bg-[#FAFAFA]">
            <div className="flex flex-col gap-1 text-xs sm:text-sm text-[#595959]">
              <p>{t('area_band_options_intro')}</p>
              <p>
                <strong>{t('area_band_option1_label')}</strong>{' '}
                {t('area_band_option1_description')}
              </p>
              <p>
                <strong>{t('area_band_option2_label')}</strong>{' '}
                {t('area_band_option2_description')}
              </p>
            </div>

            {/* Option 1 - Direct percentage */}
            <div className="flex items-stretch gap-3">
              <div className="flex justify-center items-center self-stretch shrink-0 w-8 rounded-[8px] bg-[#E9F1D9]">
                <span className="text-sm font-bold text-black whitespace-nowrap leading-none -rotate-90">
                  {t('area_band_option1_label')}
                </span>
              </div>
              <div className="flex-1">
                <div className="w-full sm:w-1/2 flex flex-col gap-1.5">
                  <label className="text-sm font-medium text-[#141414]">
                    {t('area_band_percentage_label')}
                  </label>
                  <InputNumber
                    value={
                      selections.bandPercentage
                        ? parseFloat(selections.bandPercentage)
                        : null
                    }
                    onChange={handleBandPercentageChange}
                    placeholder={t('area_band_percentage_placeholder')}
                    min={0}
                    max={100}
                    className="w-full"
                    size="large"
                    addonAfter="%"
                  />
                </div>
              </div>
            </div>

            {/* OR divider */}
            <Divider className="my-0">
              <span className="text-sm font-medium text-[#8c8c8c]">
                {t('area_band_or')}
              </span>
            </Divider>

            {/* Option 2 - Row spacing and band width on same line */}
            <div className="flex items-stretch gap-3">
              <div className="flex justify-center items-center self-stretch shrink-0 w-8 rounded-[8px] bg-[#E9F1D9]">
                <span className="text-sm font-bold text-black whitespace-nowrap leading-none -rotate-90">
                  {t('area_band_option2_label')}
                </span>
              </div>
              <div className="flex-1">
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-[#141414]">
                      {t('area_band_row_spacing_label')}
                    </label>
                    <InputNumber
                      value={
                        selections.rowSpacing
                          ? parseFloat(selections.rowSpacing)
                          : null
                      }
                      onChange={handleRowSpacingChange}
                      placeholder={t('area_band_row_spacing_placeholder')}
                      min={0}
                      className="w-full"
                      size="large"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-[#141414]">
                      {t('area_band_unit_label')}
                    </label>
                    <Select
                      showSearch
                      filterOption={false}
                      value={selections.rowSpacingUnitId || undefined}
                      onChange={handleRowSpacingUnitChange}
                      placeholder={t('area_band_unit_placeholder')}
                      options={measurementUnitOptions}
                      loading={isLoadingMeasurementUnits}
                      className="w-full"
                      size="large"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-[#141414]">
                      {t('area_band_width_label')}
                    </label>
                    <InputNumber
                      value={
                        selections.bandWidth
                          ? parseFloat(selections.bandWidth)
                          : null
                      }
                      onChange={handleBandWidthChange}
                      placeholder={t('area_band_width_placeholder')}
                      min={0}
                      className="w-full"
                      size="large"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-[#141414]">
                      {t('area_band_unit_label')}
                    </label>
                    <Select
                      showSearch
                      filterOption={false}
                      value={selections.bandWidthUnitId || undefined}
                      onChange={handleBandWidthUnitChange}
                      placeholder={t('area_band_unit_placeholder')}
                      options={measurementUnitOptions}
                      loading={isLoadingMeasurementUnits}
                      className="w-full"
                      size="large"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
);

Step2Area.displayName = 'Step2Area';

export default Step2Area;
